import { useState } from 'react';
import { useRewardsStore } from '@/store/rewards.store';
import { NurseryCard } from './NurseryCard';
import { LockedCard } from './LockedCard';
import { RARITY_CONFIG, ALL_SPECIES_IDS, SPECIES } from '@/constants/rewards';
import type { Rarity } from '@/types/rewards';

const FILTER_OPTIONS: Array<{ value: Rarity | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'common', label: 'Common' },
  { value: 'uncommon', label: 'Uncommon' },
  { value: 'rare', label: 'Rare' },
  { value: 'epic', label: 'Epic' },
  { value: 'legendary', label: 'Legendary' },
];

export function Nursery() {
  const [filter, setFilter] = useState<Rarity | 'all'>('all');
  const collection = useRewardsStore((s) => s.collection);
  const setDetailCreatureId = useRewardsStore((s) => s.setDetailCreatureId);

  const owned = collection?.ownedSpecies ?? [];
  const ownedMap = new Map(owned.map((o) => [o.speciesId, o]));

  const allEntries = ALL_SPECIES_IDS.map((id) => ({
    speciesId: id,
    rarity: SPECIES[id].rarity,
    owned: ownedMap.get(id) ?? null,
  }));

  const filtered = filter === 'all' ? allEntries : allEntries.filter((e) => e.rarity === filter);

  return (
    <section>
      <h2 className="text-2xl font-display font-bold text-stone-900 mb-1">Nursery</h2>
      <p className="text-sm text-stone-500 mb-4">Your creature collection. Tap to inspect.</p>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTER_OPTIONS.map(({ value, label }) => {
          const active = filter === value;
          const rarityConfig = value !== 'all' ? RARITY_CONFIG[value] : null;
          return (
            <button
              key={value}
              onClick={() => setFilter(value)}
              className="rounded-full px-3 py-1 text-xs font-semibold border transition-colors"
              style={
                active && rarityConfig
                  ? {
                      backgroundColor: rarityConfig.bg,
                      borderColor: rarityConfig.border,
                      color: rarityConfig.color,
                    }
                  : active
                    ? { backgroundColor: '#2E8A8A', borderColor: '#2E8A8A', color: '#fff' }
                    : { backgroundColor: '#fff', borderColor: '#E8DFD0', color: '#8A7F72' }
              }
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {filtered.map(({ speciesId, owned: ownedEntry }) =>
          ownedEntry ? (
            <NurseryCard
              key={speciesId}
              speciesId={speciesId}
              rarity={ownedEntry.rarity as Rarity}
              count={ownedEntry.count}
              onClick={() => setDetailCreatureId(speciesId)}
            />
          ) : (
            <LockedCard key={speciesId} speciesId={speciesId} />
          ),
        )}
      </div>
    </section>
  );
}
